import React, { useRef, useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useSalon } from '../../../contexts/SalonContext';
import { Card, Button, Switch } from '../../../components/ui';
import { toast } from 'react-hot-toast';
import { 
  ShareIcon, 
  ArrowDownTrayIcon,
  DocumentDuplicateIcon,
  LinkIcon,
  QrCodeIcon
} from '@heroicons/react/24/outline';

const QRCodeSettings = () => {
  const { salonId } = useSalon();
  const qrRef = useRef();
  const [showQrOptions, setShowQrOptions] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Verificar se é dispositivo móvel
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  
  // URL do salão que será codificada no QR Code
  const salonUrl = `${window.location.origin}/${salonId}`;
  
  // Função para baixar o QR Code como PNG
  const downloadQRCode = (format = 'png') => {
    try {
      const svg = qrRef.current.querySelector('svg');
      
      if (format === 'svg') {
        // Download como SVG
        const svgData = new XMLSerializer().serializeToString(svg);
        const blob = new Blob([svgData], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const downloadLink = document.createElement("a");
        downloadLink.download = `qrcode-${salonId}.svg`;
        downloadLink.href = url;
        downloadLink.click();
        URL.revokeObjectURL(url);
      } else {
        // Download como PNG
        const svgData = new XMLSerializer().serializeToString(svg);
        const img = new Image();
        
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = 512; // Tamanho fixo maior para melhor qualidade
          canvas.height = 512;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          
          const pngFile = canvas.toDataURL("image/png");
          const downloadLink = document.createElement("a");
          downloadLink.download = `qrcode-${salonId}.png`;
          downloadLink.href = pngFile;
          downloadLink.click();
        };
        
        img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
      }
      
      toast.success(`QR Code baixado com sucesso em formato ${format.toUpperCase()}!`);
    } catch (error) {
      console.error('Erro ao baixar QR Code:', error);
      toast.error('Erro ao baixar o QR Code');
    }
  };
  
  // Função para compartilhar o link do salão
  const shareLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Agende seu horário',
          text: 'Clique no link para agendar seu horário:',
          url: salonUrl
        });
        toast.success('Link compartilhado com sucesso!');
      } else {
        await navigator.clipboard.writeText(salonUrl);
        toast.success('Link copiado para a área de transferência!');
      }
    } catch (error) {
      console.error('Erro ao compartilhar:', error);
      toast.error('Erro ao compartilhar o link');
    }
  };
  
  // Função para copiar o link para a área de transferência
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(salonUrl);
      toast.success('Link copiado para a área de transferência!');
    } catch (error) {
      console.error('Erro ao copiar:', error);
      toast.error('Erro ao copiar o link');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* <h2 className="text-2xl font-semibold mb-6">Link da Agenda</h2> */}
      
      <Card className="p-6 mb-6">
        <h3 className="text-lg font-medium mb-4">Link do Salão</h3>
        
        {/* Input do link */}
        <div className="flex items-center space-x-2 mb-6">
          <input
            type="text"
            value={salonUrl}
            readOnly
            className="flex-1 p-2 border border-gray-300 rounded-md text-sm"
          />
          <Button
            onClick={copyLink}
            variant="outline"
            className="flex-shrink-0"
            title="Copiar link"
          >
            <DocumentDuplicateIcon className="h-5 w-5" />
          </Button>
        </div>
        
        {/* Botões de ação */}
        <div className="space-y-3">
          {/* Botão de compartilhar apenas em mobile */}
          {isMobile && (
            <Button
              onClick={shareLink}
              variant="outline"
              className="w-full flex items-center justify-center gap-2 text-blue-400 border-blue-400 hover:bg-blue-50"
            >
              <ShareIcon className="h-5 w-5" />
              Compartilhar Link
            </Button>
          )}
          
          {/* Toggle para mostrar opções de QR Code */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center space-x-2">
              <QrCodeIcon className="h-5 w-5 text-gray-600" />
              <span className="text-gray-800">Mostrar QR Code</span>
            </div>
            <Switch
              checked={showQrOptions}
              onChange={setShowQrOptions}
            />
          </div>
        </div>
        
        {/* Instruções de uso */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Como usar o Link da Agenda?
          </h4>
          <ul className="text-sm text-gray-600 space-y-2">
            <li>1. Copie ou compartilhe o link do salão</li>
            <li>2. Envie para seus clientes por WhatsApp ou redes sociais</li>
            <li>3. Adicione o link na sua bio do Instagram</li>
            <li>4. Seus clientes serão direcionados para a página de agendamento</li>
          </ul>
        </div>
      </Card>
      
      {/* Seção de QR Code (visível apenas quando o toggle estiver ativado) */}
      {showQrOptions && (
        <Card className="p-6">
          <h3 className="text-lg font-medium mb-4">QR Code do Link</h3>
          
          <div className="flex flex-col items-center space-y-4 mb-6">
            <div 
              ref={qrRef}
              className="bg-white p-4 rounded-lg shadow-sm"
            >
              <QRCodeSVG
                value={salonUrl}
                size={256}
                level="H"
                includeMargin={true}
              />
            </div>
          </div>
          
          <div className="space-y-3">
            <Button
              onClick={() => downloadQRCode('png')}
              variant="outline"
              className="w-full justify-center"
            >
              <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
              Baixar QR Code (PNG)
            </Button>
            
            <Button
              onClick={() => downloadQRCode('svg')}
              variant="outline"
              className="w-full justify-center"
            >
              <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
              Baixar QR Code (SVG)
            </Button>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Como usar o QR Code?
            </h4>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>1. Baixe o QR Code em alta qualidade</li>
              <li>2. Imprima e coloque em local visível no salão</li>
              <li>3. Seus clientes podem escanear com a câmera do celular</li>
              <li>4. Eles serão direcionados para a página de agendamento</li>
            </ul>
          </div>
        </Card>
      )}
    </div>
  );
};

export default QRCodeSettings; 